import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Send, Zap, UserCheck, Clock, AlertCircle, Loader2
} from 'lucide-react';
import {
  getContact, sendContactInvite, getContactEnrollments,
  getContactInvites, updateContactLifecycle, exitEnrollment
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/new-components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [contact, setContact] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);

  const loadContact = useCallback(async () => {
    if (!id) return;
    try {
      const [contactRes, enrollRes, inviteRes] = await Promise.all([
        getContact(id),
        getContactEnrollments(id).catch(() => ({ data: { data: [] } })),
        getContactInvites(id).catch(() => ({ data: { data: [] } }))
      ]);
      setContact(contactRes.data?.data || null);
      setEnrollments(enrollRes.data?.data || []);
      setInvites(inviteRes.data?.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadContact(); }, [loadContact]);

  const handleSendInvite = async () => {
    if (!id) return;
    setSendingInvite(true);
    setInviteResult(null);
    try {
      const res = await sendContactInvite(id);
      setInviteResult(res.data?.invite_url || 'Invite sent');
      loadContact();
    } catch (err: any) {
      setInviteResult(`Error: ${err.response?.data?.error || err.message}`);
    }
    setSendingInvite(false);
  };

  const handleLifecycleChange = async (stage: string) => {
    if (!id) return;
    try {
      await updateContactLifecycle(id, stage);
      loadContact();
    } catch (err) { console.error(err); }
  };

  const handleExitEnrollment = async (enrollmentId: string) => {
    try {
      await exitEnrollment(enrollmentId, 'manual');
      loadContact();
    } catch (err) { console.error(err); }
  };

  const lifecycleBadgeColor = (stage: string) => {
    const m: Record<string, string> = {
      lead: 'bg-gray-200 text-gray-800',
      invited: 'bg-blue-100 text-blue-800',
      registered: 'bg-indigo-100 text-indigo-800',
      verified: 'bg-green-100 text-green-800',
      invested: 'bg-amber-100 text-amber-800',
      repeat_investor: 'bg-purple-100 text-purple-800',
      dormant: 'bg-red-100 text-red-800'
    };
    return m[stage] || 'bg-gray-200 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!contact) return <div className="text-muted-foreground text-center py-12">Contact not found</div>;

  return (
    <div className="space-y-6">
      <Link to="/admin/contacts">
        <Button variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Contacts
        </Button>
      </Link>

      {/* Header */}
      <div className="flex items-start gap-6">
        <div className="w-16 h-16 bg-[#F0F0F0] rounded-full flex items-center justify-center border border-border">
          <span className="text-2xl font-bold text-[hsl(var(--chart-2))]">
            {(contact.first_name || '?')[0]}{(contact.last_name || '?')[0]}
          </span>
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-semibold text-[#242424]">{contact.first_name} {contact.last_name}</h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Mail className="h-4 w-4" /> {contact.email}</span>
            {contact.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> {contact.phone}</span>}
          </div>
          <div className="flex gap-2 mt-3">
            <Badge variant={contact.kyc_status === 'approved' ? 'default' : 'secondary'}>
              KYC: {contact.kyc_status || 'pending'}
            </Badge>
            <Badge variant="secondary">{contact.accreditation_status || 'unverified'}</Badge>
            {contact.lifecycle_stage && (
              <Badge className={lifecycleBadgeColor(contact.lifecycle_stage)}>
                {contact.lifecycle_stage?.replace('_', ' ')}
              </Badge>
            )}
            {contact.is_registered_user && (
              <Badge className="bg-green-100 text-green-800">
                <UserCheck className="h-3 w-3 mr-1" /> Registered User
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap w-full sm:w-auto">
          <Select
            value={contact.lifecycle_stage || 'lead'}
            onValueChange={handleLifecycleChange}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Lifecycle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="invested">Invested</SelectItem>
              <SelectItem value="repeat_investor">Repeat Investor</SelectItem>
              <SelectItem value="dormant">Dormant</SelectItem>
            </SelectContent>
          </Select>
          {!contact.is_registered_user && contact.email && (
            <Button onClick={handleSendInvite} disabled={sendingInvite}>
              {sendingInvite ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send Signup Invite
            </Button>
          )}
        </div>
      </div>

      {/* Invite Result */}
      {inviteResult && (
        <Card className={`border ${inviteResult.startsWith('Error') ? 'border-destructive' : 'border-primary'}`}>
          <CardContent className="py-3 flex items-center gap-2 text-sm">
            {inviteResult.startsWith('Error') ? (
              <AlertCircle className="h-4 w-4 text-destructive" />
            ) : (
              <Send className="h-4 w-4 text-primary" />
            )}
            <span className="font-mono text-xs break-all">{inviteResult}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setInviteResult(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="activity">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="enrollments">Enrollments ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="invites">Invites ({invites.length})</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="investments">Investments</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
        </TabsList>

        {/* Activity Feed */}
        <TabsContent value="activity">
          <Card className="border-border mt-4">
            <CardHeader>
              <CardTitle className="text-lg text-[#242424]">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {contact.activity?.length > 0 ? (
                <div className="space-y-4">
                  {(contact.activity || []).map((a: any, i: number) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                      <div>
                        <p className="text-sm text-[#242424]">{a.description || a.action}</p>
                        <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No recent activity</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enrollments */}
        <TabsContent value="enrollments">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              {enrollments.length > 0 ? (
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-xs text-muted-foreground">Campaign</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Initiative</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Step</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Enrolled</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((e: any) => (
                      <tr key={e.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-4 text-sm">{e.campaign_name}</td>
                        <td className="p-4 text-sm text-muted-foreground">{e.initiative_name}</td>
                        <td className="p-4 text-sm">{e.current_step}</td>
                        <td className="p-4">
                          <Badge variant={e.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                            {e.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{new Date(e.enrolled_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          {e.status === 'active' && (
                            <Button variant="ghost" size="sm" className="text-destructive text-xs"
                              onClick={() => handleExitEnrollment(e.id)}>
                              Exit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Zap className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p>Not enrolled in any campaigns</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invites */}
        <TabsContent value="invites">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              {invites.length > 0 ? (
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Sent</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Expires</th>
                      <th className="text-left p-4 text-xs text-muted-foreground">Accepted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((inv: any) => (
                      <tr key={inv.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-4">
                          <Badge variant={inv.status === 'accepted' ? 'default' : inv.status === 'pending' ? 'secondary' : 'destructive'} className="text-xs">
                            {inv.status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{new Date(inv.sent_at).toLocaleString()}</td>
                        <td className="p-4 text-sm text-muted-foreground">{new Date(inv.expires_at).toLocaleString()}</td>
                        <td className="p-4 text-sm text-muted-foreground">{inv.accepted_at ? new Date(inv.accepted_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p>No invites sent</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunities */}
        <TabsContent value="opportunities">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs text-muted-foreground">Deal</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Stage</th>
                    <th className="text-right p-4 text-xs text-muted-foreground">Amount</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.opportunities?.map((o: any) => (
                    <tr key={o.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-4 text-sm text-[#242424]">{o.deal_title}</td>
                      <td className="p-4">
                        <Badge variant="secondary" className="text-xs capitalize">{o.stage}</Badge>
                      </td>
                      <td className="p-4 text-sm text-[#242424] text-right">{formatCurrency(o.amount || 0)}</td>
                      <td className="p-4 text-sm text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  )) || (
                    <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No opportunities</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investments */}
        <TabsContent value="investments">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs text-muted-foreground">Deal / Property</th>
                    <th className="text-right p-4 text-xs text-muted-foreground">Amount</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.investments?.map((inv: any) => (
                    <tr key={inv.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-4 text-sm text-[#242424]">{inv.deal_title || inv.property_title || '—'}</td>
                      <td className="p-4 text-sm text-[#242424] text-right font-medium">{formatCurrency(inv.amount)}</td>
                      <td className="p-4"><Badge variant="secondary" className="text-xs">{inv.status}</Badge></td>
                      <td className="p-4 text-sm text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</td>
                    </tr>
                  )) || (
                    <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No investments</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accounts */}
        <TabsContent value="accounts">
          <Card className="border-border mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[420px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs text-muted-foreground">Entity Name</th>
                    <th className="text-left p-4 text-xs text-muted-foreground">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.entities?.map((e: any) => (
                    <tr key={e.id} className="border-b border-border hover:bg-muted/50">
                      <td className="p-4 text-sm text-[#242424]">{e.entity_name}</td>
                      <td className="p-4 text-sm text-muted-foreground">{e.entity_type}</td>
                    </tr>
                  )) || (
                    <tr><td colSpan={2} className="p-8 text-center text-muted-foreground">No accounts</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tags */}
        <TabsContent value="tags">
          <Card className="border-border mt-4">
            <CardContent className="p-6">
              <p className="text-muted-foreground text-sm">Tag management coming soon</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactDetail;
